import type { App } from '@grafikr/components';

import intersectionLoader from 'utils/loaders/intersection';
import onElementInteraction from 'utils/loaders/on-element-interaction';
import onRegionAvailable from 'utils/loaders/on-region-available';
import onState from 'utils/loaders/on-state';

import header from 'layout/header';
import productDetailMedia from 'pages/product/detail-media';

export default (app: App) => {
  app.add({
    // Layout
    header,
    'header-announcement': [
      intersectionLoader,
      () =>
        import(/* webpackChunkName: "layout-header-announcement" */ 'layout/header-announcement'),
    ],

    // Components
    'active-state': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-active-state" */ 'components/active-state'),
    ],
    'address-form': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-address-form" */ 'components/address-form'),
    ],
    animate: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-animate" */ 'components/animate'),
    ],
    carousel: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-carousel" */ 'components/carousel'),
    ],
    'cart-count': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-cart-count" */ 'components/cart-count'),
    ],
    copy: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-copy" */ 'components/copy'),
    ],
    'country-selector-form': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "component-country-selector-form" */ 'components/country-selector-form'
        ),
    ],
    'filter-form': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-filter-form" */ 'components/filter-form'),
    ],
    'fit-text': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-fit-text" */ 'components/fit-text'),
    ],
    geo: [
      onRegionAvailable,
      () => import(/* webpackChunkName: "component-geo" */ 'components/geo'),
    ],
    'lazy-content': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-lazy-content" */ 'components/lazy-content'),
    ],
    'grid-control': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-grid-control" */ 'components/grid-control'),
    ],
    marquee: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-marquee" */ 'components/marquee'),
    ],
    modal: [
      onElementInteraction,
      () => import(/* webpackChunkName: "component-modal" */ 'components/modal'),
    ],
    pagination: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-pagination" */ 'components/pagination'),
    ],
    'product-item': [
      ['mouseover', 'focusin'],
      () => import(/* webpackChunkName: "component-product-item" */ 'components/product-item'),
    ],
    'range-slider': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-range-slider" */ 'components/range-slider'),
    ],
    'rebuy-products': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-rebuy-products" */ 'components/rebuy-products'),
    ],
    'swap-content': [
      [onElementInteraction, onState],
      () => import(/* webpackChunkName: "component-swap-content" */ 'components/swap-content'),
    ],
    toggle: ['click', () => import(/* webpackChunkName: "component-toggle" */ 'components/toggle')],
    remove: ['click', () => import(/* webpackChunkName: "component-remove" */ 'components/remove')],
    video: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-video" */ 'components/video'),
    ],
    zendesk: [
      intersectionLoader,
      () => import(/* webpackChunkName": "component-zendesk" */ 'components/zendesk'),
    ],
    collapsible: [
      'click',
      () => import(/* webpackCunkName: "component-collapsible" */ 'components/collapsible'),
    ],
    'stock-availability-drawer': [
      onElementInteraction,
      () =>
        import(
          /* webpackChunkName: "component-stock-availability" */ 'components/stock-availability-drawer'
        ),
    ],
    'inventory-locations': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName": "component-inventory-locations" */ 'components/inventory-locations'
        ),
    ],

    // Pages
    cart: [intersectionLoader, () => import(/* webpackChunkName: "cart" */ 'pages/cart')],

    collection: [
      intersectionLoader,
      () => import(/* webpackChunkName: "collection-main" */ 'pages/collection/main'),
    ],
    'collection-navigation': [
      intersectionLoader,
      () => import(/* webpackChunkName: "collection-navigation" */ 'pages/collection/navigation'),
    ],
    'collection-sort': [
      intersectionLoader,
      () => import(/* webpackChunkName: "collection-sort" */ 'pages/collection/sort'),
    ],

    product: [
      intersectionLoader,
      () => import(/* webpackChunkName: "product-main" */ 'pages/product/main'),
    ],
    'product-simple': [
      intersectionLoader,
      () => import(/* webpackChunkName: "product-simple-main" */ 'pages/product/simple-main'),
    ],
    'product-detail-media': productDetailMedia,
    'product-images': [
      intersectionLoader,
      () => import(/* webpackChunkName: "product-images" */ 'pages/product/images'),
    ],
    'product-form': [
      intersectionLoader,
      () => import(/* webpackChunkName: "product-form" */ 'pages/product/form'),
    ],
    'product-comparison': [
      intersectionLoader,
      () => import(/* webpackChunkName: "product-comparison" */ 'pages/product/comparison'),
    ],
  });
};
